import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import {
  ABIERTOS,
  ESTADOS,
  cambiarEstado,
  traerPedidos,
} from "../services/pedidosSistema";
import ModoEdicion from "../components/funcionales/ModoEdicion";
import Loading from "./Loading";

/* Los pedidos al sistema — lo que se escribe con `/ticket` por WhatsApp.
 *
 * ── POR QUÉ HACÍA FALTA ────────────────────────────────────────────────────
 *
 * El pedido se guardaba y se publicaba como issue de GitHub, y sólo se miraba
 * en `/pedidos` de FlotaBot: una URL que nadie tiene a mano. Es la misma falla
 * que ya tuvimos con `/api/sync-docs` y con el botón de la planilla — **un
 * registro que existe y no está donde se trabaja es lo mismo que no estar.**
 *
 * Y el costo caía del lado del que pidió: sin forma de ver si alguien lo miró,
 * lo vuelve a pedir por WhatsApp, y volvemos al chat — que es exactamente de
 * donde esto vino a sacarlo.
 *
 * ── Y HASTA HOY MIRABA LA TABLA EQUIVOCADA ─────────────────────────────────
 *
 * Leía `public.pedidos` y el bot escribía en `public.tickets`: **de 22 ids
 * compartidos, 21 tenían texto distinto**. «P-14» decía una cosa por WhatsApp
 * y otra acá, las dos listas se veían perfectamente creíbles, y lo que se
 * marcaba resuelto desde esta pantalla no le avisaba nunca a quien lo pidió
 * —`barrer()` lee la otra—. Está en `services/pedidosSistema.js`.
 *
 * ── NO SE CARGA DESDE ACÁ ──────────────────────────────────────────────────
 *
 * Se pide con `/ticket` por WhatsApp, que es donde aparece la necesidad: uno
 * está usando el bot, algo le falta, y lo dice ahí. Un formulario acá sería un
 * segundo lugar para lo mismo, y el día que se cargue en el que no es ese
 * pedido no aparece en la lista. Es la lección de los dos talleres.
 *
 * ── Y NO SE BORRA ──────────────────────────────────────────────────────────
 *
 * Descartar deja la fila con su motivo. Que alguien haya decidido NO hacer
 * algo es parte de la historia, y hacerlo desaparecer deja la lista sin poder
 * explicar por qué eso nunca se hizo. Mismo criterio que un movimiento anulado
 * y un ticket de taller cancelado.
 */

const cuando = (iso) => {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() - 3 * 3600 * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

const TONO = {
  abierto: "mal",
  en_curso: "ojo",
  esperando: "ojo",
  resuelto: "ok",
  cerrado: "",
};

const PedidosSistema = () => {
  const { permisos = null } = useOutletContext() || {};
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(false);
  const [vista, setVista] = useState("pendientes");
  const [yendo, setYendo] = useState(null);

  /* Mientras los permisos no llegaron se asume que puede: esconder el lápiz
   * medio segundo y después mostrarlo se lee como que la pantalla titila.
   *
   * `cargar.sistema` además de `admin.permisos` porque es el que la base pide
   * de verdad (`puedo_atender_ticket` sobre el área «sistema»). Con sólo el
   * primero, alguien habilitado para mover estos pedidos no veía el lápiz —y
   * «no tengo el permiso» y «el botón no está» se ven igual, que es lo que ya
   * nos pasó con la ticketera. */
  const puedeEditar = !permisos
    || permisos.includes("admin.permisos")
    || permisos.includes("cargar.sistema");
  const tocando = puedeEditar && editando;

  /* Los dos enlaces a GitHub, sólo para quien administra. Al revés que
   * `puedeEditar`: mientras los permisos no llegaron NO se dibujan, porque
   * acá lo caro es de más y no de menos.
   *
   * El repositorio es privado: para todos los demás el enlace es un 404 con
   * pantalla de login, o sea un botón que nunca lleva a ningún lado. Y la URL
   * del repo lleva su nombre adentro, que es lo que no tiene que estar a la
   * vista. Las dos razones apuntan al mismo lado. */
  const puedeVerElRepo = Boolean(permisos?.includes?.("admin.permisos"));

  const traer = useCallback(async () => {
    try {
      setFilas(await traerPedidos());
    } catch (e) {
      setError(e.message || "No se pudieron leer los pedidos");
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer]);

  const lista = useMemo(() => {
    const todas = filas || [];
    if (vista === "todos") return todas;
    if (vista === "pendientes") {
      return todas.filter((f) => ABIERTOS.includes(f.estado));
    }
    return todas.filter((f) => f.estado === vista);
  }, [filas, vista]);

  const pendientes = (filas || []).filter((f) => ABIERTOS.includes(f.estado));

  const mover = async (id, estado) => {
    setYendo(id);
    setError(null);
    try {
      await cambiarEstado(id, estado);
      /* Se cambia en memoria y no se vuelve a pedir la lista: recargar mueve
       * todo de lugar justo después de tocar una fila. */
      setFilas((prev) =>
        (prev || []).map((f) => (f.id === id ? { ...f, estado } : f)));
    } catch (e) {
      setError(e.message || "No se pudo mover el pedido");
    } finally {
      setYendo(null);
    }
  };

  if (error && !filas) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!filas) return <Loading />;

  return (
    <div className="docs">
      <div className="tk-filtros">
        <button
          className={"tk-chip" + (vista === "pendientes" ? " activo" : "")}
          onClick={() => setVista("pendientes")}
        >
          Esperando {pendientes.length > 0 && <b>{pendientes.length}</b>}
        </button>
        {ESTADOS.map((e) => (
          <button
            key={e.clave}
            className={"tk-chip" + (vista === e.clave ? " activo" : "")}
            onClick={() => setVista(e.clave)}
          >
            {e.label}
          </button>
        ))}
        <button
          className={"tk-chip" + (vista === "todos" ? " activo" : "")}
          onClick={() => setVista("todos")}
        >
          Todos
        </button>
        <ModoEdicion
          activo={editando}
          onCambiar={setEditando}
          puede={puedeEditar}
          que="los pedidos"
        />
      </div>

      {/* `suave` y no `.panel-aviso` a secas: sin el modificador esto es un
          overlay absoluto que tapa el rail y la cabecera, y un error de
          carga dejaría la pantalla sin salida. */}
      {error && <p className="panel-aviso suave">{error}</p>}

      {lista.length === 0 && (
        <p className="panel-aviso suave">
          {vista === "pendientes" ? "No hay ningún pedido esperando." : "Nada acá."}
        </p>
      )}

      {lista.map((f) => (
        <div key={f.id} className="pf-bloque">
          <h3>
            P-{f.id}
            <span className={"pill " + (TONO[f.estado] || "")}>
              {(ESTADOS.find((e) => e.clave === f.estado) || {}).label
                || f.estado}
            </span>
            {/* El nombre viene con el rol pegado («Nico · Admin»): lo arma la
                base al abrir el ticket y se congela ahí, como en `uso_bot`.
                La gente cambia de puesto, y resolviéndolo al leer un pedido de
                marzo mostraría el rol de hoy. */}
            <span className="tk-item-meta">
              {f.quien_nombre || "sin nombre"} · {cuando(f.creado_en)}
            </span>
          </h3>

          {/* EL TEXTO VA TAL CUAL SE ESCRIBIÓ, con sus acentos y mayúsculas.
              Es lo que alguien va a leer para entender qué le pidieron, y
              normalizado se lee como un telegrama. */}
          <p className="pf-texto">{f.texto}</p>

          {/* TRES RASTROS Y NO UNO SOLO, y van etiquetados porque significan
              cosas distintas: la RESPUESTA la escribió quien pidió —es su
              conformidad—, el TRIAGE es lo que decidió la corrida, y el PR es
              el cambio. Dibujados iguales, «el que pidió dijo que sí» y «la
              corrida anotó una duda» se leerían como la misma cosa. */}
          {f.respuesta && (
            <p className="sr-nota">
              <b>Contestó quien lo pidió:</b> {f.respuesta}
            </p>
          )}
          {f.triage && (
            <p className="sr-nota">
              <b>Del triage:</b> {f.triage}
            </p>
          )}
          {puedeVerElRepo && f.pr_url && (
            <p className="tk-item-meta">
              <a href={f.pr_url} target="_blank" rel="noopener noreferrer">
                Ver el cambio
              </a>
            </p>
          )}

          {/* EL ISSUE ES UNA CONSECUENCIA Y NO UN REQUISITO: si GitHub no
              contestó, el pedido igual quedó guardado. Por eso el enlace se
              dibuja sólo si está, y su ausencia no se marca como un error. */}
          {puedeVerElRepo && f.issue_url && (
            <p className="tk-item-meta">
              <a href={f.issue_url} target="_blank" rel="noopener noreferrer">
                Ver en GitHub
              </a>
            </p>
          )}

          {tocando && (
            <div className="sr-acciones">
              {ESTADOS.filter((e) => e.clave !== f.estado).map((e) => (
                <button
                  key={e.clave}
                  type="button"
                  className="btn-suave"
                  disabled={yendo === f.id}
                  onClick={() => mover(f.id, e.clave)}
                >
                  {e.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      <p className="docs-pie">
        Se piden por WhatsApp con <code>/ticket</code>.
      </p>
    </div>
  );
};

export default PedidosSistema;
